import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Download, Image, Clock, Calendar } from 'lucide-react'
import { api } from '../lib/api'
import { formatTimestamp, formatDuration, getStatusVariant, getStatusIcon } from '../lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useState, useEffect } from 'react'

// Component to display artifact using public URL
function ArtifactDisplay({ artifact, type }: { artifact: any, type: 'image' | 'video' }) {
  const [error, setError] = useState<string | null>(null)

  // Use public URL if available
  const publicUrl = artifact.publicUrl

  useEffect(() => {
    if (!publicUrl) {
      setError('Artifact URL not available')
    } else {
      console.log(`Using public URL for artifact ${artifact.id}: ${publicUrl}`)
    }
  }, [artifact.id, publicUrl])



  if (error || !publicUrl) {
    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
          <span className="text-gray-500">{error || 'Artifact URL not available'}</span>
        </div>
        <div className="p-3 bg-gray-50 flex items-center justify-between">
          <span className="text-xs text-gray-600">
            {artifact.size_bytes ?
              type === 'video' ?
                `${Math.round(artifact.size_bytes / (1024 * 1024))} MB` :
                `${Math.round(artifact.size_bytes / 1024)} KB`
              : 'Unknown size'
            }
          </span>
          <span className="text-xs text-gray-500">Download unavailable</span>
        </div>
      </div>
    )
  }

  const handleDownload = async () => {
    try {
      if (!publicUrl) {
        throw new Error('No artifact URL available for download')
      }

      // Fetch from public URL and download
      const response = await fetch(publicUrl)
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url
      a.download = `${artifact.kind}_${artifact.id}.${artifact.kind === 'recording' ? 'mp4' : 'png'}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download failed:', err)
      alert(`Download failed: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      {type === 'image' ? (
        <img
          src={publicUrl}
          alt={`Screenshot ${artifact.id}`}
          className="w-full h-auto max-h-96 object-contain bg-gray-50"
        />
      ) : (
        <video
          src={publicUrl}
          controls
          className="w-full h-auto max-h-96 bg-black"
          preload="metadata"
        >
          Your browser does not support the video tag.
        </video>
      )}
      <div className="p-3 bg-gray-50 flex items-center justify-between">
        <span className="text-xs text-gray-600">
          {artifact.size_bytes ?
            type === 'video' ?
              `${Math.round(artifact.size_bytes / (1024 * 1024))} MB` :
              `${Math.round(artifact.size_bytes / 1024)} KB`
            : 'Unknown size'
          }
        </span>
        <button
          onClick={handleDownload}
          className="inline-flex items-center text-xs text-blue-600 hover:text-blue-500"
        >
          <Download className="h-3 w-3 mr-1" />
          Download
        </button>
      </div>
    </div>
  )
}

export function RunDetail() {
  const { id } = useParams<{ id: string }>()
  const runId = parseInt(id!)

  const { data: run, isLoading } = useQuery({
    queryKey: ['run', runId],
    queryFn: () => api.getRun(runId),
    enabled: !!runId,
  })

  // Debug the run data to see if artifacts have dataUrl
  useEffect(() => {
    if (run?.artifacts) {
      console.log('Run artifacts:', run.artifacts.map((a: any) => ({
        id: a.id,
        kind: a.kind,
        size_bytes: a.size_bytes,
        hasPublicUrl: !!a.publicUrl,
        publicUrl: a.publicUrl
      })))
    }
  }, [run])

  // Test authentication for debugging
  const testAuth = async () => {
    try {
      const response = await fetch(`/api/runs/${runId}/artifacts/test`, {
        credentials: 'include'
      })
      const data = await response.json()
      console.log('Auth test result:', data)
      alert(`Auth test: ${response.ok ? 'SUCCESS' : 'FAILED'}\n${JSON.stringify(data, null, 2)}`)
    } catch (err) {
      console.error('Auth test failed:', err)
      alert(`Auth test failed: ${err}`)
    }
  }

  // Test artifact loading for debugging
  const testArtifact = async () => {
    if (!run?.artifacts?.length) {
      alert('No artifacts to test')
      return
    }

    const artifact = run.artifacts[0] // Test first artifact
    try {
      console.log('Testing artifact:', artifact)
      const response = await fetch(`/api/runs/${runId}/artifacts/${artifact.id}/view`, {
        credentials: 'include'
      })

      console.log('Response status:', response.status)
      console.log('Response headers:', Object.fromEntries(response.headers.entries()))

      if (!response.ok) {
        const errorText = await response.text()
        alert(`Artifact test FAILED: ${response.status} - ${errorText}`)
        return
      }

      const blob = await response.blob()
      console.log('Blob details:', { size: blob.size, type: blob.type })

      // Try to create a data URL and test it
      const url = URL.createObjectURL(blob)
      console.log('Object URL created:', url)

      // Open in new tab to verify content
      window.open(url, '_blank')

      alert(`Artifact test SUCCESS!\nSize: ${blob.size} bytes\nType: ${blob.type}\nOpened in new tab`)

    } catch (err) {
      console.error('Artifact test failed:', err)
      alert(`Artifact test failed: ${err}`)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Skeleton className="h-8 w-8" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent className="space-y-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="grid grid-cols-2 gap-4">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  if (!run) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Run not found</h3>
        <p className="mt-1 text-sm text-gray-500">
          The run you're looking for doesn't exist.
        </p>
        <div className="mt-6">
          <Link
            to="/runs"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Back to Runs
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/runs">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={testAuth}>
            Test Auth
          </Button>
          <Button variant="outline" size="sm" onClick={testArtifact}>
            Test Artifact
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Run #{run.id}</h1>
            <div className="flex items-center space-x-4 mt-2">
              <Badge variant={getStatusVariant(run.status)}>
                {getStatusIcon(run.status)} {run.status}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {run.test_name} • {run.trigger}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Run Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Run Details</CardTitle>
              <CardDescription>
                Execution information and timing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    Started
                  </dt>
                  <dd className="mt-1 text-sm font-medium">
                    {run.started_at ? formatTimestamp(run.started_at) : 'Not started'}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-muted-foreground flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    Finished
                  </dt>
                  <dd className="mt-1 text-sm font-medium">
                    {run.finished_at ? formatTimestamp(run.finished_at) : 'Not finished'}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-muted-foreground flex items-center">
                    <Clock className="h-4 w-4 mr-2" />
                    Duration
                  </dt>
                  <dd className="mt-1 text-sm font-medium">
                    {run.duration_ms ? formatDuration(run.duration_ms) : 'N/A'}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-muted-foreground">Trigger</dt>
                  <dd className="mt-1 text-sm font-medium capitalize">{run.trigger}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          {/* Steps */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Test Steps</h3>
            
            {run.steps?.length ? (
              <div className="space-y-4">
                {run.steps.map((step, index) => (
                  <div key={step.id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <Badge variant={getStatusVariant(step.status)}>
                          {step.status}
                        </Badge>
                      </div>
                      {step.duration_ms && (
                        <span className="text-xs text-gray-500">
                          {formatDuration(step.duration_ms)}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-900 mb-2">{step.instruction}</p>
                    
                    {step.selector && (
                      <p className="text-xs text-gray-500 font-mono bg-gray-50 p-2 rounded">
                        {step.selector}
                      </p>
                    )}
                    
                    {step.screenshot && (
                      <div className="mt-3">
                        <button className="inline-flex items-center text-xs text-blue-600 hover:text-blue-500">
                          <Image className="h-3 w-3 mr-1" />
                          View Screenshot
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-gray-500">No steps recorded</p>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Artifacts */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Artifacts</h3>

            {run.artifacts?.length ? (
              <div className="space-y-6">
                {/* Screenshots */}
                {run.artifacts.filter(a => a.kind === 'screenshot').length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                      <Image className="h-4 w-4 mr-2" />
                      Screenshots ({run.artifacts.filter(a => a.kind === 'screenshot').length})
                    </h4>
                    <div className="grid grid-cols-1 gap-4">
                      {run.artifacts.filter(a => a.kind === 'screenshot').map((artifact) => (
                        <ArtifactDisplay
                          key={artifact.id}
                          artifact={artifact}
                          type="image"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Recordings */}
                {run.artifacts.filter(a => a.kind === 'recording').length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                      <Download className="h-4 w-4 mr-2" />
                      Recordings ({run.artifacts.filter(a => a.kind === 'recording').length})
                    </h4>
                    <div className="space-y-4">
                      {run.artifacts.filter(a => a.kind === 'recording').map((artifact) => (
                        <ArtifactDisplay
                          key={artifact.id}
                          artifact={artifact}
                          type="video"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Other Files */}
                {run.artifacts.filter(a => a.kind !== 'screenshot' && a.kind !== 'recording').length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Other Files ({run.artifacts.filter(a => a.kind !== 'screenshot' && a.kind !== 'recording').length})
                    </h4>
                    <div className="space-y-2">
                      {run.artifacts.filter(a => a.kind !== 'screenshot' && a.kind !== 'recording').map((artifact) => (
                        <div key={artifact.id} className="flex items-center justify-between p-3 border rounded">
                          <div>
                            <p className="text-sm font-medium text-gray-900 capitalize">
                              {artifact.kind}
                            </p>
                            <p className="text-xs text-gray-500">
                              {artifact.size_bytes ? `${Math.round(artifact.size_bytes / 1024)} KB` : 'Unknown size'}
                            </p>
                          </div>
                          <a
                            href={`/api/runs/${run.id}/artifacts/${artifact.id}/download`}
                            download
                            className="inline-flex items-center text-xs text-blue-600 hover:text-blue-500"
                          >
                            <Download className="h-3 w-3 mr-1" />
                            Download
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-sm text-gray-500">No artifacts</p>
              </div>
            )}
          </div>

          {/* Test Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Test Info</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-500">Test Name</dt>
                <dd className="text-sm font-medium text-gray-900">
                  <Link
                    to={`/tests/${run.test_id}`}
                    className="text-blue-600 hover:text-blue-500"
                  >
                    {run.test_name}
                  </Link>
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-500">Base URL</dt>
                <dd className="text-sm font-medium text-gray-900">
                  <a
                    href={run.test_base_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-500 truncate block"
                  >
                    {run.test_base_url}
                  </a>
                </dd>
              </div>
              {run.requested_by_email && (
                <div>
                  <dt className="text-sm text-gray-500">Requested By</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {run.requested_by_email}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  )
}
